import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as editableDocument from 'utils/model/editableDocument';
import flow from 'lodash.flow';
import { updateDocumentScoringFinancialIndicators } from '../actionCreators';
//inner components
import DocumentScoringIndicatorListComponent from './DocumentScoringIndicatorListComponent';
import useEventCallback from 'utils/hooks/useEventCallback';
import { collection } from 'utils/immutable';
import { isMandatory, foreachProp } from 'utils/simple-validations';
import { when, prop } from 'utils/fn-combinators';

const getItemValidationRules = () => ({
  values: foreachProp(when(prop('isMandatory'), [isMandatory()]))
});

export const DocumentScoringIndicatorListContainer = ({
  documentId,
  siteId,
  documentScoringGridId,
  scoringClassificationId,
  engineScoringId,
  indicatorList,
  disabled,
  entityType,
  isHistory,
  periodList,
  actions
}) => {
  const [state, setState] = useState({
    showModal: false,
    savingModal: false,
    loading: false,
    saving: false,
    localIndicatorList: indicatorList.map(editableDocument.create),
    localPeriodList: periodList
  });

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      localPeriodList: periodList
    }));
  }, [periodList]);

  const handleFinancialIndicatorValueChange = useEventCallback(
    (idx, periodId, value) => {
      const indicatorToUpdate = state.localIndicatorList[idx];
      const values = {
        ...indicatorToUpdate.document.values,
        [periodId]: value === '' ? null : value
      };

      const updateAndValidateItem = flow(
        editableDocument.update('values', values, null),
        editableDocument.validate(getItemValidationRules(), false)
      );

      const localIndicatorList = collection.update(idx, updateAndValidateItem, state.localIndicatorList);

      setState({
        ...state,
        localIndicatorList
      });
    },
    [state]
  );

  const onSaveFinancialIndicators = async () => {
    if (!state.localIndicatorList || state.localIndicatorList.length === 0) return;

    setState({
      ...state,
      saving: true
    });

    try {
      const event = await actions.updateDocumentScoringFinancialIndicators(
        documentId,
        siteId,
        documentScoringGridId,
        entityType,
        engineScoringId,
        scoringClassificationId,
        state.localIndicatorList.map(x => x.document),
        state.localPeriodList
      );
      if (event.ok) {
        setState({
          ...state,
          saving: false,
          localIndicatorList: state.localIndicatorList.map(flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo))
        });
      } else {
        setState({ ...state, saving: false });
      }
    } catch (error) {
      setState({ ...state, saving: false });
    }
  }

  const isValid = state.localIndicatorList.every(x => x.validationInfo.isValid);

  return (
    <DocumentScoringIndicatorListComponent
      isValid={isValid}
      saving={state.saving}
      disabled={disabled}
      isHistory={isHistory}
      indicatorList={state.localIndicatorList || []}
      periodList={state.localPeriodList || []}
      handleFinancialIndicatorValueChange={handleFinancialIndicatorValueChange}
      onSaveFinancialIndicators={onSaveFinancialIndicators}
    />
  );
};

DocumentScoringIndicatorListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  documentScoringGridId: PropTypes.number.isRequired,
  scoringClassificationId: PropTypes.number.isRequired,
  engineScoringId: PropTypes.number.isRequired,
  indicatorList: PropTypes.array.isRequired,
  periodList: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  entityType: PropTypes.string.isRequired,
  isHistory: PropTypes.bool,
  actions: PropTypes.object.isRequired
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ updateDocumentScoringFinancialIndicators }, dispatch)
  };
}

export default compose(connect(null, mapDispatchToProps))(DocumentScoringIndicatorListContainer);
